import { useCallback, useEffect, useState } from "react";

import {
	Box,
	Button,
	Checkbox,
	IconButton,
	Popover,
	Typography,
} from "@mui/material";
import classNames from "classnames";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { IoMdAdd, IoMdCopy } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";

import { daysOfWeek as listDaysOfWeek } from "../../../../../constants";
import useScreenSize from "../../../../../hooks/useScreenSize";
import { TAvailableHour } from "../../../../../hooks/useTeachingPolicy";
import { TimePicker } from "../../../../common/TimePicker/TimePicker";

export type TGeneralAvailabilityPerDayProps = {
	dayOfWeek: {
		text: string;
		value: number;
	};
	availableHours: TAvailableHour[];
	handleChangeAvailableHours: (
		daysOfWeek: number[],
		availableHours: TAvailableHour[],
	) => void;
};

export function GeneralAvailabilityPerDay({
	dayOfWeek,
	availableHours,
	handleChangeAvailableHours,
}: TGeneralAvailabilityPerDayProps) {
	const { width } = useScreenSize();
	const isMobile = width <= 768;

	const [copyPopoverAnchorEl, setCopyPopoverAnchorEl] =
		useState<HTMLElement | null>(null);
	const [selectedDay, setSelectedDay] = useState({
		0: false,
		1: false,
		2: false,
		3: false,
		4: false,
		5: false,
		6: false,
		[dayOfWeek.value]: true,
	});

	const defaultValues = {
		timeBlocks: availableHours.map((availableHour) => {
			const selectedStartTime = `${availableHour.startHour * 60 + availableHour.startMinute}`;
			let selectedEndTime = `${availableHour.endHour * 60 + availableHour.endMinute}`;
			if (selectedEndTime === "0") {
				selectedEndTime = `${24 * 60}`;
			}
			return {
				selectedStartTime,
				selectedEndTime,
			};
		}),
	};
	const { control, trigger } = useForm({ defaultValues });

	const { fields, append, remove } = useFieldArray({
		control,
		name: "timeBlocks",
	});
	const { timeBlocks } = useWatch({ control });

	const getNewAvailableHoursOfDay = useCallback(() => {
		const minuteArray = Array.from({ length: 24 * 60 + 1 }).fill(false);

		if (!timeBlocks) {
			return [];
		}

		for (let i = 0; i < timeBlocks.length; i++) {
			const start = Number(timeBlocks[i].selectedStartTime);
			const end = Number(timeBlocks[i].selectedEndTime);

			for (let j = start; j <= end; j++) {
				minuteArray[j] = true;
			}
		}

		const newAvailableHoursOfDay = [];
		let start = null;
		for (let i = 0; i < minuteArray.length; i++) {
			if (start === null && minuteArray[i]) {
				start = i;
			} else if (start !== null && !minuteArray[i]) {
				newAvailableHoursOfDay.push({
					dayOfWeek: dayOfWeek.value,
					startHour: Math.floor(start / 60),
					startMinute: start % 60,
					endHour: Math.floor((i - 1) / 60),
					endMinute: (i - 1) % 60,
				});
				start = null;
			}
		}

		if (start !== null) {
			newAvailableHoursOfDay.push({
				dayOfWeek: dayOfWeek.value,
				startHour: Math.floor(start / 60),
				startMinute: start % 60,
				endHour: 0,
				endMinute: 0,
			});
		}

		return newAvailableHoursOfDay;
	}, [dayOfWeek.value, timeBlocks]);

	useEffect(() => {
		const onTimeBlockChange = async () => {
			const isValid = await trigger("timeBlocks");

			if (!isValid) {
				return;
			}

			const newAvailableHoursOfDay = getNewAvailableHoursOfDay();

			const currentString = availableHours
				.map(
					(availableHour) =>
						`${availableHour.startHour}-${availableHour.startMinute}-${availableHour.endHour}-${availableHour.endMinute}`,
				)
				.join("|");
			const newString = newAvailableHoursOfDay
				.map(
					(availableHour) =>
						`${availableHour.startHour}-${availableHour.startMinute}-${availableHour.endHour}-${availableHour.endMinute}`,
				)
				.join("|");

			if (currentString === newString) {
				return;
			}

			handleChangeAvailableHours([dayOfWeek.value], newAvailableHoursOfDay);
		};

		onTimeBlockChange();
	}, [
		availableHours,
		dayOfWeek,
		getNewAvailableHoursOfDay,
		handleChangeAvailableHours,
		timeBlocks,
		trigger,
	]);

	const onClickAddTimeSlot = async () => {
		const isValid = await trigger("timeBlocks");

		if (!isValid) {
			return;
		}

		append({ selectedStartTime: "", selectedEndTime: "" });
	};

	const onCloseCopyPopover = () => {
		setCopyPopoverAnchorEl(null);
		setSelectedDay({
			0: false,
			1: false,
			2: false,
			3: false,
			4: false,
			5: false,
			6: false,
			[dayOfWeek.value]: true,
		});
	};

	const onClickApplyCopy = () => {
		const newAvailableHoursOfDay = getNewAvailableHoursOfDay();
		const daysOfWeek = [];
		const newAvailableHours = [];
		for (let i = 0; i < listDaysOfWeek.length; i++) {
			if (
				listDaysOfWeek[i].value !== dayOfWeek.value &&
				selectedDay[listDaysOfWeek[i].value]
			) {
				daysOfWeek.push(listDaysOfWeek[i].value);
				newAvailableHours.push(
					...newAvailableHoursOfDay.map((availableHours) => ({
						...availableHours,
						dayOfWeek: listDaysOfWeek[i].value,
					})),
				);
			}
		}

		handleChangeAvailableHours(daysOfWeek, newAvailableHours);
		onCloseCopyPopover();
	};

	const renderAvailableHours = () => {
		return (
			<div className="available-hours-container">
				{fields.map((field, index) => {
					return (
						<div key={field.id} className="available-hour-item-container">
							<TimePicker
								control={control}
								name={`timeBlocks.${index}.selectedStartTime`}
								rules={{
									required: {
										value: true,
										message: "Invalid time.",
									},
									validate: (value, formValues) => {
										if (
											Number(value) >=
												Number(formValues.timeBlocks[index].selectedEndTime) &&
											formValues.timeBlocks[index].selectedEndTime !== ""
										) {
											return "Choose an end time later than the start time.";
										}

										return;
									},
								}}
							/>
							<div className="time-separator">
								<Typography className="normal-text">-</Typography>
							</div>
							<TimePicker
								control={control}
								name={`timeBlocks.${index}.selectedEndTime`}
								rules={{
									required: {
										value: true,
										message: "Invalid time.",
									},
									validate: (value, formValues) => {
										if (
											Number(value) <
											Number(formValues.timeBlocks[index].selectedStartTime)
										) {
											return " ";
										}

										return;
									},
								}}
							/>
							<div className="delete-button">
								<IconButton
									className="icon-button"
									onClick={() => {
										remove(index);
									}}
									sx={{
										padding: 0,
										marginRight: 0,
										marginLeft: 1,
									}}
									disableRipple>
									<IoClose />
								</IconButton>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	const renderButtonGroup = () => {
		let applyButtonDisabled = true;
		for (let i = 0; i < listDaysOfWeek.length; i++) {
			if (
				listDaysOfWeek[i].value !== dayOfWeek.value &&
				selectedDay[listDaysOfWeek[i].value]
			) {
				applyButtonDisabled = false;
				break;
			}
		}

		return (
			<Box display="flex" gap="16px">
				<div>
					<IconButton
						className="icon-button add-button"
						onClick={onClickAddTimeSlot}>
						<IoMdAdd />
					</IconButton>
				</div>
				<div>
					<IconButton
						className="icon-button add-button"
						onClick={async (event) => {
							const target = event.currentTarget;
							const isValid = await trigger("timeBlocks");

							if (!isValid) {
								return;
							}

							setCopyPopoverAnchorEl(target);
						}}>
						<IoMdCopy />
					</IconButton>
					<Popover
						open={Boolean(copyPopoverAnchorEl)}
						anchorEl={copyPopoverAnchorEl}
						className="copy-popover-container"
						onClose={onCloseCopyPopover}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: -100,
						}}>
						<div className="copy-popover-content-container">
							{listDaysOfWeek.map((day) => {
								return (
									<Box display="flex" alignItems="center" key={day.text}>
										<Checkbox
											checked={selectedDay[day.value]}
											disabled={day.value === dayOfWeek.value}
											onChange={() => {
												setSelectedDay((prev) => ({
													...prev,
													[day.value]: !prev[day.value],
												}));
											}}
											icon={<MdCheckBoxOutlineBlank className="normal-icon" />}
											checkedIcon={<MdCheckBox className="normal-icon" />}
										/>
										<Typography className="normal-text">{day.text}</Typography>
									</Box>
								);
							})}
							<Button
								disabled={applyButtonDisabled}
								onClick={onClickApplyCopy}
								className={classNames("normal-button no-transform", {
									"primary-button": !applyButtonDisabled,
									"disabled-button": applyButtonDisabled,
								})}>
								Apply
							</Button>
						</div>
					</Popover>
				</div>
			</Box>
		);
	};

	if (isMobile) {
		return (
			<div className="general-availability-day-item item-separator-small">
				<Box display="flex" flex="1" justifyContent="space-between">
					<Typography className="normal-text bold general-availability-day-text">
						{dayOfWeek.text}
					</Typography>
					{renderButtonGroup()}
				</Box>
				<Box display="flex" flex="1" justifyContent="space-between">
					{renderAvailableHours()}
				</Box>
			</div>
		);
	}

	return (
		<div className="general-availability-day-item item-separator-small">
			<Typography className="normal-text bold general-availability-day-text">
				{dayOfWeek.text}
			</Typography>
			<Box display="flex" flex="1" justifyContent="space-between">
				{renderAvailableHours()}
				{renderButtonGroup()}
			</Box>
		</div>
	);
}
